import type { Integration, IntegrationCategory, IntegrationRegion } from "./integrationTypes";

/**
 * Mock data for the Integration transactions page.
 *
 * This file is the ONLY place that fakes server data for Integrations.
 * When the backend is wired up, each MOCK_* export is replaced by an
 * API fetch via useIntegrations(). No UI component should be touched.
 *
 * Status reflects the current signed-in user's connection state. In
 * mock, Stripe and Shopify are hardcoded "connected" so stakeholders
 * can see both scenes (empty + populated) without switching users.
 */

export const MOCK_INTEGRATIONS: Integration[] = [
  // Payment processors
  { id: "stripe", name: "Stripe", brandId: "stripe", category: "payment_processor", region: "global", description: "Automatically import Stripe charges, refunds, payouts, and fees.", status: "connected", lastSyncedAt: "2026-10-20T21:30:00Z" },
  { id: "paypal", name: "PayPal", brandId: "paypal", category: "payment_processor", region: "global", description: "Automatically import PayPal sales, refunds, and transfer activity.", status: "available" },
  { id: "square", name: "Square", brandId: "square", category: "payment_processor", region: "global", description: "Pull Square payment transactions, refunds, and daily deposits.", status: "available" },
  { id: "adyen", name: "Adyen", brandId: "adyen", category: "payment_processor", region: "global", description: "Enterprise payment data: cards, local methods, settlements.", status: "available" },
  { id: "braintree", name: "Braintree", brandId: "braintree", category: "payment_processor", region: "global", description: "PayPal's merchant platform. Imports transactions and disputes.", status: "available" },
  { id: "mollie", name: "Mollie", brandId: "mollie", category: "payment_processor", region: "europe", description: "iDEAL, Bancontact, SEPA Direct Debit, and more.", status: "available" },
  { id: "razorpay", name: "Razorpay", brandId: "razorpay", category: "payment_processor", region: "india", description: "UPI, cards, netbanking, and wallets for Indian businesses.", status: "available" },

  // E-commerce
  { id: "shopify", name: "Shopify", brandId: "shopify", category: "ecommerce", region: "global", description: "Import orders, refunds, shipping, tax, and payouts from your store.", status: "connected", lastSyncedAt: "2026-10-20T19:30:00Z" },
  { id: "woocommerce", name: "WooCommerce", brandId: "woocommerce", category: "ecommerce", region: "global", description: "Pull orders and refunds from your WordPress store.", status: "available" },
  { id: "bigcommerce", name: "BigCommerce", brandId: "bigcommerce", category: "ecommerce", region: "global", description: "Imports orders, discounts, and tax from BigCommerce.", status: "available" },
  { id: "wix", name: "Wix", brandId: "wix", category: "ecommerce", region: "global", description: "Pull Wix Stores transactions and booking payments.", status: "available" },
  { id: "squarespace", name: "Squarespace", brandId: "squarespace", category: "ecommerce", region: "global", description: "Commerce orders and subscription payments.", status: "available" },
  { id: "magento", name: "Magento", brandId: "magento", category: "ecommerce", region: "global", description: "Adobe Commerce orders, invoices, credit memos.", status: "available" },

  // Marketplaces
  { id: "amazon", name: "Amazon Seller", brandId: "amazon", category: "marketplace", region: "global", description: "Settlement reports, FBA fees, and advertising spend.", status: "available" },
  { id: "etsy", name: "Etsy", brandId: "etsy", category: "marketplace", region: "global", description: "Order payments, Etsy Payments statements, listing fees.", status: "available" },
  { id: "ebay", name: "eBay", brandId: "ebay", category: "marketplace", region: "global", description: "Managed payments and eBay seller fees.", status: "available" },
  { id: "walmart", name: "Walmart Marketplace", brandId: "walmart", category: "marketplace", region: "us_canada", description: "Marketplace settlements and seller fees.", status: "available" },
  { id: "mercadolibre", name: "Mercado Libre", brandId: "mercadopago", category: "marketplace", region: "latam", description: "Latin America's largest marketplace: orders, fees, Mercado Pago.", status: "available" },

  // POS
  { id: "clover", name: "Clover", brandId: "clover", category: "pos", region: "global", description: "Clover POS daily sales and refunds.", status: "available" },
  { id: "lightspeed", name: "Lightspeed", brandId: "lightspeed", category: "pos", region: "global", description: "Retail and restaurant POS daily totals.", status: "available" },

  // Billing
  { id: "chargebee", name: "Chargebee", brandId: "chargebee", category: "billing", region: "global", description: "Subscription billing, invoices, MRR events.", status: "available" },
  { id: "recurly", name: "Recurly", brandId: "recurly", category: "billing", region: "global", description: "Subscription lifecycle revenue and credits.", status: "available" },
];

/** Human-readable labels for each category. */
export const CATEGORY_LABELS: Record<IntegrationCategory, string> = {
  payment_processor: "Payment processors",
  ecommerce: "E-commerce platforms",
  marketplace: "Marketplaces",
  pos: "POS",
  billing: "Billing",
};

/** Order categories appear in the UI (section headers and chip row). */
export const CATEGORY_ORDER: IntegrationCategory[] = [
  "payment_processor",
  "ecommerce",
  "marketplace",
  "pos",
  "billing",
];

/** Human-readable labels for each region (shown under the integration name). */
export const REGION_LABELS: Record<IntegrationRegion, string> = {
  global: "Global",
  us: "US",
  canada: "Canada",
  us_canada: "US, Canada",
  europe: "Europe",
  india: "India",
  latam: "LATAM",
  asia_pacific: "Asia Pacific",
};